<script setup lang="ts">
import {
  Building07,
  Users03,
  Trash01,
  AlarmClock,
  Clipboard,
  LayoutLeft,
  File06,
  Truck01,
  BookOpen01,
  PieChart01,
  Calendar,
  Laptop01,
  Folder,
  Building05,
  ClipboardCheck,
} from "utruckin-icons";
import { useRoute, useRouter } from "vue-router";
import { ref, Component } from "vue";
import { useNotification } from "@/utils/notification";
import logo from "@/assets/images/logo.svg";
import miniLogo from "@/assets/images/logo-mini.svg";
import { useUserStore } from "@/features/users/user.store";

interface IMainItem {
  name: string;
  router: string;
  icon: Component;
  isVisible: boolean;
}

const router = useRouter();
const route = useRoute();
const usersStore = useUserStore();
const { pushNotification } = useNotification();

const mainItems: IMainItem[] = [
  {
    name: "All tasks",
    router: "/all-tasks",
    icon: File06,
    isVisible: true,
  },
  {
    name: "My tasks",
    router: "/my-tasks",
    icon: Clipboard,
    isVisible: true,
  },
  {
    name: "Reviews",
    router: "/reviews",
    icon: ClipboardCheck,
    isVisible:
      usersStore.userProfile?.role == "super_admin" ||
      usersStore.userProfile?.role == "admin" ||
      usersStore.userProfile?.role == "supervisor",
  },
  {
    name: "Users",
    router: "/users",
    icon: Users03,
    isVisible: true,
  },
  {
    name: "Companies",
    router: "/companies",
    icon: Building07,
    isVisible: true,
  },
  {
    name: "Drivers",
    router: "/drivers",
    icon: Truck01,
    isVisible: true,
  },
  {
    name: "Shifts",
    router: "/shift",
    icon: Clipboard,
    isVisible: true,
  },
  {
    name: "Work types",
    router: "/work-types",
    icon: File06,
    isVisible: true,
  },
  {
    name: "Archive",
    router: "/archive",
    icon: Trash01,
    isVisible: true,
  },
  {
    name: "Inspections",
    router: "/inspections",
    icon: BookOpen01,
    isVisible: true,
  },
  {
    name: "Providers",
    router: "/provider",
    icon: Laptop01,
    isVisible: usersStore.userProfile?.role == "super_admin",
  },
  {
    name: "Watch list",
    router: "/watch-list",
    icon: AlarmClock,
    isVisible: true,
  },
  {
    name: "Work schedule",
    router: "/work-schedule",
    icon: Calendar,
    isVisible: true,
  },
  {
    name: "Management",
    router: "/management",
    icon: Folder,
    isVisible:
      usersStore.userProfile?.role == "super_admin" ||
      usersStore.userProfile?.role == "admin" ||
      usersStore.userProfile?.role == "supervisor",
  },
  {
    name: "Live statistics",
    router: "/live-stats",
    icon: PieChart01,
    isVisible: true,
  },
];

const isWide = ref<boolean>(true);

const goToPage = (item: IMainItem) => {
  router.push(item.router);
};
</script>

<template>
  <div class="c-w-sidebar" :class="isWide ? 'wide' : ''">
    <div class="header">
      <div class="header__body">
        <img
          v-if="isWide"
          :src="logo"
          width="142"
          height="36"
          alt="company_logo"
          class="company__logo"
        />
        <img
          v-else
          :src="miniLogo"
          width="24"
          height="24"
          alt="company_mini_logo"
          class="company__logo-mini"
        />
        <div
          class="header__icon"
          @click="
            () => {
              isWide = !isWide;
            }
          "
        >
          <div class="icon">
            <component :is="LayoutLeft"></component>
          </div>
        </div>
      </div>
    </div>

    <div class="body">
      <div v-for="(item, index) in mainItems" :key="index" class="body__list">
        <div
          v-if="item.isVisible"
          class="body__list--item"
          :class="route.path == item.router ? 'active' : ''"
          @click="goToPage(item)"
        >
          <component class="icon" :is="item.icon" :size="20" />
          {{ isWide ? item.name : "" }}
        </div>

        <div v-if="route.path == item.router" class="body__list--child"></div>
        <div v-if="!isWide" class="body__list--hover">
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-w-sidebar {
  width: 72px;
  display: flex;
  flex-direction: column;
  height: 100vh;
  transition: 0.2s linear;

  &.wide {
    width: 240px;

    .header {
      height: 72px;
      justify-content: left;
      padding: 18px 16px;

      &__body {
        width: 208px;
        height: 56px;
        padding: 10px 12px;
        background: var(--color-system-background);
      }

      &__icon {
        position: relative;
        width: 20px;
        height: 20px;
        left: 0;
        background: var(--color-system-background);
      }

      .icon {
        width: 20px;
        height: 20px;
      }
    }
  }

  .header {
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    &__body {
      width: 52px;
      height: 56px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: var(--color-system-background);
      border-radius: var(--base-radius-20);
      padding: 10px 14px;
    }

    .company__logo {
      width: 111px;
      height: 36px;
    }

    .company__logo-mini {
      width: 22px;
      height: 24px;
    }

    &__icon {
      position: absolute;
      width: 24px;
      height: 24px;
      background: var(--color-system-background);
      left: calc(100% - 22px);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-radius: var(--base-radius-12);
    }

    .icon {
      color: var(--color-label-secondary);
      width: 16px;
      height: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .body {
    height: 100%;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &__list {
      position: relative;

      &--item {
        height: 44px;
        padding: 10px;
        cursor: pointer;
        border-radius: 14px;
        display: flex;
        gap: 10px;
        align-items: center;
        font-weight: 500;
        color: var(--color-label-secondary);

        &.active {
          background: var(--color-tertiary-system-background);
          color: var(--color-label) !important;
          border: 1px solid var(--color-border);
        }

        &:hover {
          color: var(--color-label);
        }
      }

      &--child {
        position: absolute;
        right: -16px;
        top: calc(50% - 13px);
        width: 4px;
        height: 26px;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      &--hover {
        height: 28px;
        display: none;
        position: absolute;
        left: calc(100% + 8px);
        top: calc(50% - 13px);
        z-index: 3;
        padding: 4px 8px;
        background: var(--color-white);
        box-shadow: 0 0 8px 0 var(--color-shadow);
        color: var(--color-label);
        font-weight: 500;
        border-radius: 8px;
        white-space: nowrap;
      }

      &:hover .body__list--hover {
        display: flex;
      }
    }
  }

  .footer {
    height: 72px;
    padding: 16px;
    border-top: 1px solid var(--color-border);

    &__item {
      padding: 10px;
      cursor: pointer;
      border-radius: 14px;
      display: flex;
      gap: 10px;
      align-items: center;

      &:hover {
        background-color: var(--color-hover);
      }
    }
  }
}
</style>
